import { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, postAPI, approvalAPI, analyticsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  FileText, Calendar, CheckCircle2, BarChart3, Clock,
  TrendingUp, Eye, ArrowUpRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PostDetailSheet } from '../components/PostDetailSheet';
import { formatDateLong } from '../lib/dateFormat';

const statusColors = {
  draft: 'bg-secondary text-secondary-foreground',
  in_review: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  approved: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
  scheduled: 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20',
  published: 'bg-primary/15 text-primary border border-primary/20',
  rejected: 'bg-rose-500/15 text-rose-400 border border-rose-500/20',
};

export default function ClientDashboardPage() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);

  const fetchData = () => {
    if (currentWorkspace?.id) {
      setLoading(true);
      Promise.all([
        dashboardAPI.get(currentWorkspace.id),
        postAPI.list(currentWorkspace.id),
        approvalAPI.list(currentWorkspace.id, 'pending'),
      ])
        .then(([dashRes, postsRes, appRes]) => {
          setData(dashRes.data);
          setPosts(postsRes.data.items || []);
          setApprovals(appRes.data || []);
        })
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentWorkspace?.id]);

  const stats = data?.stats || {};

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-secondary rounded" />
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-secondary rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in-up">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">
            Welcome, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Here's an overview of <span className="text-gold font-medium">{currentWorkspace?.name}</span>'s social media activity.
          </p>
        </div>
        {approvals.length > 0 && (
          <Button onClick={() => navigate('/approvals')} className="bg-gold hover:bg-gold/90">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {approvals.length} Pending Review{approvals.length !== 1 ? 's' : ''}
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center mb-3">
              <FileText className="h-4 w-4 text-gold" />
            </div>
            <p className="text-2xl font-display font-semibold" data-font="display">{stats.total_posts || 0}</p>
            <p className="text-xs text-muted-foreground">Total Posts</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center mb-3">
              <TrendingUp className="h-4 w-4 text-emerald-400" />
            </div>
            <p className="text-2xl font-display font-semibold" data-font="display">{stats.published || 0}</p>
            <p className="text-xs text-muted-foreground">Published</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center mb-3">
              <Clock className="h-4 w-4 text-cyan-400" />
            </div>
            <p className="text-2xl font-display font-semibold" data-font="display">{stats.scheduled || 0}</p>
            <p className="text-xs text-muted-foreground">Scheduled</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center mb-3">
              <CheckCircle2 className="h-4 w-4 text-amber-400" />
            </div>
            <p className="text-2xl font-display font-semibold" data-font="display">{stats.pending_approvals || 0}</p>
            <p className="text-xs text-muted-foreground">Needs Approval</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Approvals */}
        <Card className="bg-card/60 backdrop-blur">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-amber-400" />
                Pending Your Review
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/approvals')} className="text-xs">
                View All <ArrowUpRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {approvals.length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">All caught up! No pending reviews.</p>
              </div>
            ) : (
              approvals.slice(0, 5).map(a => (
                <div
                  key={a.id}
                  className="p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedPost(a.post ? { ...a.post, approval_id: a.id } : null)}
                  data-testid={`approval-item-${a.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{a.post?.title || 'Untitled Post'}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {a.post?.content || a.note || 'No content'}
                      </p>
                    </div>
                    <Badge className="text-xs bg-amber-500/15 text-amber-400 border border-amber-500/20">
                      Review
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Upcoming Content */}
        <Card className="bg-card/60 backdrop-blur">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="h-4 w-4 text-cyan-400" />
                Content Pipeline
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')} className="text-xs">
                View Calendar <ArrowUpRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {posts.length === 0 ? (
              <div className="text-center py-6">
                <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No content planned yet.</p>
              </div>
            ) : (
              posts.slice(0, 5).map(post => (
                <div key={post.id} className="p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedPost(post)}
                  data-testid={`content-item-${post.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{post.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {(post.platforms || []).slice(0, 3).map(p => (
                          <span key={p} className={`text-xs platform-${p} capitalize`}>{p}</span>
                        ))}
                        {post.scheduled_at && (
                          <span className="text-xs text-muted-foreground">
                            {formatDateLong(post.scheduled_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge className={`text-xs ${statusColors[post.status] || 'bg-secondary'}`}>
                      {post.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Navigation */}
      <Card className="bg-card/60 backdrop-blur">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-gold" />
            <div>
              <p className="font-medium text-sm">Your Client Portal</p>
              <p className="text-xs text-muted-foreground mt-1">
                You can view the content calendar, review and approve posts, and track analytics for your brand. 
                Use the sidebar to navigate between sections.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <PostDetailSheet
        post={selectedPost}
        open={!!selectedPost}
        onOpenChange={open => { if (!open) setSelectedPost(null); }}
        onStatusChange={() => {
          setSelectedPost(null);
          fetchData();
        }}
      />
    </div>
  );
}
